import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Activity,
  ArrowUpRight,
  Building2,
  ChevronRight,
  Clock3,
  Eye,
  KeyRound,
  LayoutDashboard,
  Lock,
  LogOut,
  MoreHorizontal,
  PlusCircle,
  Search,
  Settings2,
  ShieldCheck,
  Users,
  X
} from 'lucide-react';
import api from '../services/api';
import './SuperAdminDashboard.css';

const RECENT_ACTIVITY_DAYS = 7;
const STALE_ACTIVITY_DAYS = 30;

const formatDateTime = (value) => {
  if (!value) return 'Never';
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return 'Never';
  return new Intl.DateTimeFormat('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit'
  }).format(parsed);
};

const formatRelativeTime = (value) => {
  if (!value) return 'Never';
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return 'Never';
  const diffMs = parsed.getTime() - Date.now();
  const diffMinutes = Math.round(diffMs / (1000 * 60));
  const formatter = new Intl.RelativeTimeFormat('en', { numeric: 'auto' });
  const ranges = [
    { unit: 'day', value: Math.round(diffMinutes / (60 * 24)) },
    { unit: 'hour', value: Math.round(diffMinutes / 60) },
    { unit: 'minute', value: diffMinutes }
  ];
  const matched = ranges.find((entry) => Math.abs(entry.value) >= 1) || { unit: 'minute', value: 0 };
  return formatter.format(matched.value, matched.unit);
};

const isOlderThanDays = (value, days) => {
  if (!value) return true;
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return true;
  return parsed.getTime() < (Date.now() - (days * 24 * 60 * 60 * 1000));
};

const normalizeAuditActionLabel = (action) => {
  const safe = String(action || '').trim();
  if (!safe) return 'Activity';
  return safe
    .split('_')
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(' ');
};

const getCompanyHealth = (company) => {
  const reasons = [];
  const ownerLocked = Boolean(company.owner_locked_until && new Date(company.owner_locked_until).getTime() > Date.now());
  const ownerResetRequired = Boolean(company.owner_force_password_reset);
  const neverLoggedIn = !company.last_login_at;
  const stale = !neverLoggedIn && isOlderThanDays(company.last_login_at, STALE_ACTIVITY_DAYS);
  const disabled = company.is_active !== 1;

  if (disabled) reasons.push('Disabled');
  if (ownerLocked) reasons.push('Owner locked');
  if (ownerResetRequired) reasons.push('Owner reset pending');
  if (neverLoggedIn) reasons.push('No login yet');
  if (stale) reasons.push('No recent activity');

  if (disabled) {
    return {
      tone: 'disabled',
      label: 'Disabled',
      reasons,
      needsAttention: true,
      ownerLocked,
      ownerResetRequired,
      neverLoggedIn,
      stale
    };
  }

  if (reasons.length > 0) {
    return {
      tone: 'attention',
      label: 'Attention',
      reasons,
      needsAttention: true,
      ownerLocked,
      ownerResetRequired,
      neverLoggedIn,
      stale
    };
  }

  return {
    tone: 'healthy',
    label: 'Healthy',
    reasons: ['Operating normally'],
    needsAttention: false,
    ownerLocked,
    ownerResetRequired,
    neverLoggedIn,
    stale
  };
};

const getActivityScore = (company) => (
  Number(company.transaction_count || 0) * 3
  + Number(company.order_count || 0) * 5
  + Number(company.user_count || 0)
);

function SuperAdminDashboard({ user, onLogout }) {
  const [companies, setCompanies] = useState([]);
  const [recentAuditLogs, setRecentAuditLogs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [deletingId, setDeletingId] = useState(null);
  const [confirmTarget, setConfirmTarget] = useState(null);
  const [confirmText, setConfirmText] = useState('');
  const [confirmChecked, setConfirmChecked] = useState(false);
  const [resetTarget, setResetTarget] = useState(null);
  const [unlockTarget, setUnlockTarget] = useState(null);
  const [labelsTarget, setLabelsTarget] = useState(null);
  const [labelsLoading, setLabelsLoading] = useState(false);
  const [labelsSaving, setLabelsSaving] = useState(false);
  const [labelsForm, setLabelsForm] = useState(null);
  const [workspaceCache, setWorkspaceCache] = useState({});
  const [workspaceLoadingId, setWorkspaceLoadingId] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [healthFilter, setHealthFilter] = useState('all');
  const [sortBy, setSortBy] = useState('recent_activity');
  const [detailCompanyId, setDetailCompanyId] = useState(null);
  const [actionMenuCompanyId, setActionMenuCompanyId] = useState(null);
  const [toast, setToast] = useState(null);

  const loadDashboardData = async () => {
    setLoading(true);
    setToast(null);
    try {
      const [companyData, auditData] = await Promise.all([
        api.getCompanies(),
        api.getAuditLogs({ limit: 12, sort: 'date_desc' })
      ]);
      setCompanies(companyData);
      setRecentAuditLogs(auditData);
    } catch (err) {
      setToast({ type: 'error', message: err.message });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadDashboardData();
  }, []);

  useEffect(() => {
    if (!toast) return undefined;
    const timer = setTimeout(() => setToast(null), 4000);
    return () => clearTimeout(timer);
  }, [toast]);

  useEffect(() => {
    if (!actionMenuCompanyId) return undefined;
    const handleClick = (event) => {
      if (event.target.closest?.('.superadmin-action-menu')) return;
      setActionMenuCompanyId(null);
    };
    document.addEventListener('click', handleClick);
    return () => document.removeEventListener('click', handleClick);
  }, [actionMenuCompanyId]);

  const loadWorkspaceAccess = useCallback(async (company) => {
    if (!company?.id) return null;
    if (workspaceCache[company.id]) return workspaceCache[company.id];
    setWorkspaceLoadingId(company.id);
    try {
      const data = await api.getCompanyLabels(company.id);
      const nextData = {
        tabs: Array.isArray(data.dashboard_tabs)
          ? data.dashboard_tabs.map((tab) => ({
              id: tab.id,
              label: tab.label || tab.id,
              visible: tab.visible
            }))
          : [],
        fields: Array.isArray(data.transaction_schema?.fields)
          ? data.transaction_schema.fields.map((field) => ({
              key: field.key,
              label: field.label || field.key,
              type: field.type
            }))
          : [],
        lists: Array.isArray(data.custom_lists)
          ? data.custom_lists.map((list) => ({
              id: list.id,
              list_key: list.list_key,
              label: list.label || list.list_key
            }))
          : []
      };
      setWorkspaceCache((prev) => ({ ...prev, [company.id]: nextData }));
      return nextData;
    } finally {
      setWorkspaceLoadingId(null);
    }
  }, [workspaceCache]);

  useEffect(() => {
    if (companies.length === 0 || labelsTarget) return;
    const params = new URLSearchParams(window.location.search);
    const workspaceCompanyId = Number(params.get('workspaceCompanyId'));
    if (!Number.isInteger(workspaceCompanyId) || workspaceCompanyId <= 0) return;
    const company = companies.find((entry) => Number(entry.id) === workspaceCompanyId);
    if (!company) return;

    setDetailCompanyId(company.id);
    setLabelsTarget(company);
    setLabelsLoading(true);
    setLabelsForm(null);
    loadWorkspaceAccess(company)
      .then((workspace) => {
        setLabelsForm(workspace);
      })
      .catch((err) => {
        setToast({ type: 'error', message: err.message });
        setLabelsTarget(null);
      })
      .finally(() => {
        setLabelsLoading(false);
      });

    params.delete('workspaceCompanyId');
    const nextSearch = params.toString();
    const nextUrl = `${window.location.pathname}${nextSearch ? `?${nextSearch}` : ''}`;
    window.history.replaceState({}, '', nextUrl);
  }, [companies, labelsTarget, loadWorkspaceAccess]);

  const handleDeleteCompany = async (company) => {
    setToast(null);
    setDeletingId(company.id);
    try {
      await api.deleteCompany(company.id);
      setCompanies((prev) => prev.filter((item) => item.id !== company.id));
      setRecentAuditLogs((prev) => prev.filter((item) => item.target_company_id !== company.id && item.actor_company_id !== company.id));
      if (detailCompanyId === company.id) {
        setDetailCompanyId(null);
      }
      setToast({ type: 'success', message: `Deleted ${company.name}.` });
    } catch (err) {
      setToast({ type: 'error', message: err.message });
    } finally {
      setDeletingId(null);
    }
  };

  const handleToggleStatus = async (company) => {
    setToast(null);
    try {
      const updated = await api.updateCompanyStatus(company.id, !company.is_active);
      setCompanies((prev) =>
        prev.map((item) =>
          item.id === company.id
            ? { ...item, is_active: updated.is_active, disabled_at: updated.disabled_at }
            : item
        )
      );
      setToast({ type: 'success', message: `${company.name} ${updated.is_active ? 'enabled' : 'disabled'}.` });
    } catch (err) {
      setToast({ type: 'error', message: err.message });
    }
  };

  const handleResetAdmin = async (company) => {
    setToast(null);
    try {
      await api.resetCompanyAdmin(company.id);
      setCompanies((prev) =>
        prev.map((item) =>
          item.id === company.id
            ? { ...item, owner_force_password_reset: true }
            : item
        )
      );
      setToast({ type: 'success', message: `Owner account password reset for ${company.name}.` });
    } catch (err) {
      setToast({ type: 'error', message: err.message });
    }
  };

  const handleUnlockAdmin = async (company) => {
    setToast(null);
    try {
      await api.unlockCompanyAdmin(company.id);
      setCompanies((prev) =>
        prev.map((item) =>
          item.id === company.id
            ? { ...item, owner_locked_until: null }
            : item
        )
      );
      setToast({ type: 'success', message: `Owner account unlocked for ${company.name}.` });
    } catch (err) {
      setToast({ type: 'error', message: err.message });
    }
  };

  const handleImpersonate = async (company) => {
    setToast(null);
    try {
      const data = await api.impersonateCompanyAdmin(company.id);
      const currentToken = api.getToken();
      const currentRefreshToken = api.getRefreshToken();
      const currentUser = localStorage.getItem('user');
      if (currentToken && currentUser) {
        localStorage.setItem('superadmin_token', currentToken);
        if (currentRefreshToken) {
          localStorage.setItem('superadmin_refresh_token', currentRefreshToken);
        }
        localStorage.setItem('superadmin_user', currentUser);
      }
      localStorage.setItem('impersonation_token', data.token);
      if (data.refresh_token) {
        localStorage.setItem('impersonation_refresh_token', data.refresh_token);
      }
      localStorage.setItem('impersonation_user', JSON.stringify(data.user));
      window.open('/impersonate', '_blank', 'noopener');
    } catch (err) {
      setToast({ type: 'error', message: err.message });
    }
  };

  const openWorkspaceAccess = async (company) => {
    setToast(null);
    setLabelsTarget(company);
    setLabelsLoading(true);
    setLabelsForm(null);
    try {
      const workspace = await loadWorkspaceAccess(company);
      setLabelsForm(workspace);
    } catch (err) {
      setToast({ type: 'error', message: err.message });
      setLabelsTarget(null);
    } finally {
      setLabelsLoading(false);
    }
  };

  const closeWorkspaceAccess = () => {
    setLabelsTarget(null);
    setLabelsForm(null);
    setLabelsLoading(false);
    setLabelsSaving(false);
  };

  const handleSaveWorkspaceAccess = async () => {
    if (!labelsTarget || !labelsForm) return;
    setLabelsSaving(true);
    setToast(null);
    try {
      await api.updateCompanyLabels(labelsTarget.id, {
        dashboard_tabs: labelsForm.tabs.map((tab) => ({ id: tab.id, label: tab.label, visible: tab.visible !== false })),
        transaction_fields: labelsForm.fields.map((field) => ({ key: field.key, label: field.label })),
        custom_lists: labelsForm.lists.map((list) => ({ id: list.id, label: list.label }))
      });

      setWorkspaceCache((prev) => ({ ...prev, [labelsTarget.id]: labelsForm }));
      setCompanies((prev) =>
        prev.map((company) =>
          company.id === labelsTarget.id
            ? {
                ...company,
                enabled_tab_count: labelsForm.tabs.filter((tab) => tab.visible !== false).length
              }
            : company
        )
      );

      setToast({ type: 'success', message: `Workspace access updated for ${labelsTarget.name}.` });
      closeWorkspaceAccess();
    } catch (err) {
      setToast({ type: 'error', message: err.message });
      setLabelsSaving(false);
    }
  };

  const closeConfirm = () => {
    setConfirmTarget(null);
    setConfirmText('');
    setConfirmChecked(false);
  };

  const detailedCompanies = useMemo(() => companies.map((company) => ({
    ...company,
    health: getCompanyHealth(company)
  })), [companies]);

  const attentionCompanies = useMemo(
    () => detailedCompanies.filter((company) => company.health.needsAttention),
    [detailedCompanies]
  );

  const companiesActiveRecently = useMemo(
    () => detailedCompanies.filter((company) => !isOlderThanDays(company.last_login_at, RECENT_ACTIVITY_DAYS)).length,
    [detailedCompanies]
  );

  const dormantCompanies = useMemo(
    () => detailedCompanies.filter((company) => company.health.neverLoggedIn || company.health.stale).length,
    [detailedCompanies]
  );

  const filteredCompanies = useMemo(() => {
    const term = searchTerm.trim().toLowerCase();
    return detailedCompanies.filter((company) => {
      const matchesTerm = !term
        || company.name.toLowerCase().includes(term)
        || company.slug.toLowerCase().includes(term);

      const matchesStatus =
        statusFilter === 'all'
        || (statusFilter === 'active' && company.is_active === 1)
        || (statusFilter === 'disabled' && company.is_active !== 1);

      const matchesHealth =
        healthFilter === 'all'
        || (healthFilter === 'attention' && company.health.needsAttention)
        || (healthFilter === 'locked_owner' && company.health.ownerLocked)
        || (healthFilter === 'reset_required' && company.health.ownerResetRequired)
        || (healthFilter === 'never_logged_in' && company.health.neverLoggedIn)
        || (healthFilter === 'stale' && company.health.stale);

      return matchesTerm && matchesStatus && matchesHealth;
    }).sort((left, right) => {
      if (sortBy === 'name_asc') return left.name.localeCompare(right.name);
      if (sortBy === 'newest') return new Date(right.created_at).getTime() - new Date(left.created_at).getTime();
      if (sortBy === 'most_transactions') return Number(right.transaction_count || 0) - Number(left.transaction_count || 0);
      if (sortBy === 'attention') {
        const leftScore = (left.health.needsAttention ? 1000 : 0) + getActivityScore(left);
        const rightScore = (right.health.needsAttention ? 1000 : 0) + getActivityScore(right);
        return rightScore - leftScore;
      }
      const leftActivity = left.last_login_at ? new Date(left.last_login_at).getTime() : 0;
      const rightActivity = right.last_login_at ? new Date(right.last_login_at).getTime() : 0;
      return rightActivity - leftActivity;
    });
  }, [detailedCompanies, healthFilter, searchTerm, sortBy, statusFilter]);

  const detailCompany = useMemo(
    () => detailedCompanies.find((company) => company.id === detailCompanyId) || null,
    [detailCompanyId, detailedCompanies]
  );

  useEffect(() => {
    if (!detailCompany) return;
    if (workspaceCache[detailCompany.id]) return;
    loadWorkspaceAccess(detailCompany).catch((err) => {
      setToast({ type: 'error', message: err.message });
    });
  }, [detailCompany, loadWorkspaceAccess, workspaceCache]);

  const detailWorkspace = detailCompany ? workspaceCache[detailCompany.id] : null;
  const detailAuditLogs = useMemo(() => {
    if (!detailCompany) return [];
    return recentAuditLogs.filter((entry) =>
      Number(entry.target_company_id) === Number(detailCompany.id)
      || Number(entry.actor_company_id) === Number(detailCompany.id)
    ).slice(0, 6);
  }, [detailCompany, recentAuditLogs]);

  const workspaceSummaryLabels = useMemo(() => {
    if (!detailWorkspace?.tabs) return [];
    return detailWorkspace.tabs
      .filter((tab) => tab.visible !== false)
      .slice(0, 6)
      .map((tab) => tab.label || tab.id);
  }, [detailWorkspace]);

  return (
    <div className="super-admin-dashboard">
      {toast && (
        <div className={`toast toast-${toast.type}`}>
          <span>{toast.message}</span>
          <button type="button" onClick={() => setToast(null)}>
            <X size={14} />
          </button>
        </div>
      )}

      <header className="super-admin-header">
        <div className="super-admin-hero-copy">
        <div className="super-admin-kicker">Super Admin Control Center</div>
        <h1>Manage company health, access, and workspace rollout.</h1>
        <p>Monitor activity, resolve owner-account issues, and open any company workspace without digging through raw admin tables.</p>
        <p className="helper-text">Signed in as {user.username}</p>
      </div>
        <div className="super-admin-header-actions">
          <button
            type="button"
            className="primary-button"
            onClick={() => window.open('/superadmin/setup', '_blank', 'noopener')}
          >
            <PlusCircle className="btn-icon" size={16} />
            <span>Create Company</span>
          </button>
          <button
            type="button"
            className="secondary-button"
            onClick={() => window.open('/superadmin/audit', '_blank', 'noopener')}
          >
            <ArrowUpRight className="btn-icon" size={16} />
            <span>Open Audit Logs</span>
          </button>
          <button type="button" className="logout-button" onClick={onLogout}>
            <LogOut className="btn-icon" size={16} />
            <span>Logout</span>
          </button>
        </div>
      </header>

      <section className="super-admin-hero-grid">
        <article className="super-admin-panel super-admin-panel-hero">
          <div className="super-admin-panel-heading">
            <div>
              <h2>Launch a company workspace</h2>
              <p className="helper-text">The setup wizard provisions owner/admin access, workspace tabs, transaction schema, lists, and onboarding defaults.</p>
            </div>
          </div>
          <div className="hero-feature-list">
            <span><ShieldCheck size={15} /> Owner and admin accounts</span>
            <span><LayoutDashboard size={15} /> Company-specific tabs and labels</span>
            <span><Activity size={15} /> Transaction and product schema</span>
            <span><Settings2 size={15} /> Thresholds, orders, and challan defaults</span>
          </div>
        </article>

        <article className="super-admin-panel">
          <div className="super-admin-panel-heading">
            <div>
              <h2>Recent Activity</h2>
              <p className="helper-text">Latest super-admin and company-level changes across the portfolio.</p>
            </div>
          </div>
          <div className="activity-feed">
            {recentAuditLogs.length === 0 ? (
              <div className="status-message">No recent audit activity.</div>
            ) : recentAuditLogs.map((entry) => (
              <button
                type="button"
                key={entry.id}
                className="activity-row"
                onClick={() => window.open('/superadmin/audit', '_blank', 'noopener')}
              >
                <div className="activity-icon"><Activity size={15} /></div>
                <div className="activity-copy">
                  <strong>{normalizeAuditActionLabel(entry.action)}</strong>
                  <span>
                    {entry.target_company_slug || entry.actor_company_slug || 'system'} · {formatRelativeTime(entry.created_at)}
                  </span>
                </div>
                <ChevronRight className="activity-chevron" size={16} />
              </button>
            ))}
          </div>
        </article>
      </section>

      <section className="super-admin-panel summary-grid">
        <div className="summary-card">
          <span>Total Companies</span>
          <strong>{detailedCompanies.length}</strong>
        </div>
        <div className="summary-card summary-card-alert">
          <span>Needs Attention</span>
          <strong>{attentionCompanies.length}</strong>
        </div>
        <div className="summary-card">
          <span>Active in 7d</span>
          <strong>{companiesActiveRecently}</strong>
        </div>
        <div className="summary-card">
          <span>Dormant / Never Logged In</span>
          <strong>{dormantCompanies}</strong>
        </div>
      </section>

      <section className="super-admin-strip-grid">
        <article className="super-admin-panel">
          <div className="super-admin-panel-heading">
            <div>
              <h2>Attention Queue</h2>
              <p className="helper-text">Companies that need a super-admin action next.</p>
            </div>
          </div>
          <div className="attention-list">
            {attentionCompanies.length === 0 ? (
              <div className="status-message">No companies need attention right now.</div>
            ) : attentionCompanies.slice(0, 6).map((company) => (
              <button
                type="button"
                key={company.id}
                className="attention-row"
                onClick={() => setDetailCompanyId(company.id)}
              >
                <div className="attention-row-main">
                  <strong>{company.name}</strong>
                  <span>{company.health.reasons.join(' · ')}</span>
                </div>
                <span className={`status-pill ${company.health.tone}`}>{company.health.label}</span>
              </button>
            ))}
          </div>
        </article>

        <article className="super-admin-panel">
          <div className="super-admin-panel-heading">
            <div>
              <h2>Quick Access</h2>
              <p className="helper-text">Jump into active companies and workspace tools.</p>
            </div>
          </div>
          <div className="quick-access-list">
            {filteredCompanies.slice(0, 4).map((company) => (
              <div key={company.id} className="quick-access-card">
                <div>
                  <strong>{company.name}</strong>
                  <span>@{company.slug}</span>
                </div>
                <div className="quick-access-actions">
                  <button type="button" className="secondary-button" onClick={() => setDetailCompanyId(company.id)}>
                    <Eye className="btn-icon" size={15} />
                    <span>Open</span>
                  </button>
                  <button
                    type="button"
                    className="primary-button"
                    disabled={company.is_active !== 1}
                    onClick={() => handleImpersonate(company)}
                  >
                    <ArrowUpRight className="btn-icon" size={15} />
                    <span>Impersonate</span>
                  </button>
                </div>
              </div>
            ))}
          </div>
        </article>
      </section>

      <section className="super-admin-panel">
        <div className="company-header-row">
          <div>
            <h2>Companies</h2>
            <p className="helper-text">Click a company row to inspect owner access, workspace rollout, and recent events.</p>
          </div>
          <div className="company-filters company-filters-extended">
            <label className="company-filter-field">
              <Search size={14} />
              <input
                type="text"
                value={searchTerm}
                onChange={(event) => setSearchTerm(event.target.value)}
                placeholder="Search by name or slug"
              />
            </label>
            <select value={statusFilter} onChange={(event) => setStatusFilter(event.target.value)}>
              <option value="all">All statuses</option>
              <option value="active">Active only</option>
              <option value="disabled">Disabled only</option>
            </select>
            <select value={healthFilter} onChange={(event) => setHealthFilter(event.target.value)}>
              <option value="all">All health states</option>
              <option value="attention">Needs attention</option>
              <option value="locked_owner">Locked owner</option>
              <option value="reset_required">Owner reset pending</option>
              <option value="never_logged_in">Never logged in</option>
              <option value="stale">No recent activity</option>
            </select>
            <select value={sortBy} onChange={(event) => setSortBy(event.target.value)}>
              <option value="recent_activity">Recent activity</option>
              <option value="attention">Attention first</option>
              <option value="most_transactions">Most transactions</option>
              <option value="newest">Newest companies</option>
              <option value="name_asc">Name A-Z</option>
            </select>
          </div>
        </div>

        {loading ? (
          <div className="status-message">Loading companies...</div>
        ) : filteredCompanies.length === 0 ? (
          <div className="status-message">No companies match your current filters.</div>
        ) : (
          <div className="company-table company-table-modern">
            <div className="company-row company-row-modern header">
              <span>Company</span>
              <span>Health</span>
              <span>Usage</span>
              <span>Last Activity</span>
              <span>Workspace</span>
              <span>Actions</span>
            </div>
            {filteredCompanies.map((company) => (
              <div
                key={company.id}
                className={`company-row company-row-modern ${detailCompanyId === company.id ? 'active' : ''}`}
                role="button"
                tabIndex={0}
                onClick={() => setDetailCompanyId(company.id)}
                onKeyDown={(event) => {
                  if (event.key === 'Enter' || event.key === ' ') {
                    event.preventDefault();
                    setDetailCompanyId(company.id);
                  }
                }}
              >
                <span className="company-cell company-cell-company">
                  <strong>{company.name}</strong>
                  <small>@{company.slug} · {company.timezone || 'Default timezone'}</small>
                </span>

                <span className="company-cell">
                  <span className={`status-pill ${company.health.tone}`}>{company.health.label}</span>
                  <small>{company.health.reasons.slice(0, 2).join(' · ')}</small>
                </span>

                <span className="company-cell company-usage-cells">
                  <span className="company-metric-chip"><Users size={13} /> {company.user_count} users</span>
                  <span className="company-metric-chip"><LayoutDashboard size={13} /> {company.order_count} orders</span>
                  <span className="company-metric-chip"><Activity size={13} /> {company.transaction_count} txns</span>
                </span>

                <span className="company-cell">
                  <strong>{formatRelativeTime(company.last_login_at)}</strong>
                  <small>{formatDateTime(company.last_login_at)}</small>
                </span>

                <span className="company-cell">
                  <strong>{company.enabled_tab_count || 0} tabs enabled</strong>
                  <small>{company.owner_force_password_reset ? 'Owner reset pending' : 'Workspace configured'}</small>
                </span>

                <span className="company-cell company-cell-actions" onClick={(event) => event.stopPropagation()}>
                  <button
                    type="button"
                    className="primary-button compact-button"
                    disabled={company.is_active !== 1}
                    onClick={() => handleImpersonate(company)}
                  >
                    <ArrowUpRight className="btn-icon" size={14} />
                    <span>Impersonate</span>
                  </button>
                  <button
                    type="button"
                    className="secondary-button compact-button"
                    onClick={() => openWorkspaceAccess(company)}
                  >
                    <Settings2 className="btn-icon" size={14} />
                    <span>Workspace Access</span>
                  </button>
                  <div className="superadmin-action-menu">
                    <button
                      type="button"
                      className="secondary-button icon-button"
                      onClick={() => setActionMenuCompanyId((prev) => (prev === company.id ? null : company.id))}
                    >
                      <MoreHorizontal size={16} />
                    </button>
                    {actionMenuCompanyId === company.id && (
                      <div className="superadmin-action-dropdown">
                        <button type="button" onClick={() => {
                          setActionMenuCompanyId(null);
                          handleToggleStatus(company);
                        }}
                        >
                          {company.is_active ? 'Disable Company' : 'Enable Company'}
                        </button>
                        <button type="button" onClick={() => {
                          setActionMenuCompanyId(null);
                          setResetTarget(company);
                        }}
                        >
                          Reset Owner
                        </button>
                        <button type="button" onClick={() => {
                          setActionMenuCompanyId(null);
                          setUnlockTarget(company);
                        }}
                        >
                          Unlock Owner
                        </button>
                        <button type="button" className="danger-option" onClick={() => {
                          setActionMenuCompanyId(null);
                          setConfirmTarget(company);
                        }}
                        >
                          Delete Company
                        </button>
                      </div>
                    )}
                  </div>
                </span>
              </div>
            ))}
          </div>
        )}
      </section>

      {detailCompany && (
        <div className="superadmin-drawer-backdrop" onClick={() => setDetailCompanyId(null)}>
          <aside className="superadmin-drawer" onClick={(event) => event.stopPropagation()}>
            <div className="superadmin-drawer-header">
              <div>
                <div className="drawer-kicker">Company Detail</div>
                <h3>{detailCompany.name}</h3>
                <p>@{detailCompany.slug} · {detailCompany.timezone || 'Default timezone'}</p>
              </div>
              <button type="button" className="icon-button ghost-icon-button" onClick={() => setDetailCompanyId(null)}>
                <X size={18} />
              </button>
            </div>

            <div className="superadmin-drawer-actions">
              <button
                type="button"
                className="primary-button"
                disabled={detailCompany.is_active !== 1}
                onClick={() => handleImpersonate(detailCompany)}
              >
                <ArrowUpRight className="btn-icon" size={15} />
                <span>Impersonate Company</span>
              </button>
              <button
                type="button"
                className="secondary-button"
                onClick={() => openWorkspaceAccess(detailCompany)}
              >
                <Settings2 className="btn-icon" size={15} />
                <span>Workspace Access</span>
              </button>
            </div>

            <div className="drawer-stat-grid">
              <div className="drawer-stat-card">
                <span>Status</span>
                <strong>{detailCompany.health.label}</strong>
              </div>
              <div className="drawer-stat-card">
                <span>Users</span>
                <strong>{detailCompany.user_count}</strong>
              </div>
              <div className="drawer-stat-card">
                <span>Orders</span>
                <strong>{detailCompany.order_count}</strong>
              </div>
              <div className="drawer-stat-card">
                <span>Transactions</span>
                <strong>{detailCompany.transaction_count}</strong>
              </div>
            </div>

            <section className="drawer-section">
              <div className="drawer-section-header">
                <h4>Owner Access Health</h4>
                <span className={`status-pill ${detailCompany.health.tone}`}>{detailCompany.health.label}</span>
              </div>
              <div className="drawer-badge-row">
                <span className={`drawer-badge ${detailCompany.health.ownerLocked ? 'warn' : 'ok'}`}>
                  <Lock size={13} />
                  {detailCompany.health.ownerLocked ? 'Owner locked' : 'Owner unlocked'}
                </span>
                <span className={`drawer-badge ${detailCompany.health.ownerResetRequired ? 'warn' : 'ok'}`}>
                  <KeyRound size={13} />
                  {detailCompany.health.ownerResetRequired ? 'Password reset pending' : 'Password healthy'}
                </span>
                <span className={`drawer-badge ${detailCompany.is_active === 1 ? 'ok' : 'warn'}`}>
                  <Building2 size={13} />
                  {detailCompany.is_active === 1 ? 'Company active' : 'Company disabled'}
                </span>
              </div>
              <div className="drawer-action-groups">
                <div className="drawer-action-group">
                  <span className="drawer-group-label">Operational</span>
                  <button
                    type="button"
                    className="secondary-button"
                    disabled={detailCompany.is_active !== 1}
                    onClick={() => handleImpersonate(detailCompany)}
                  >
                    <Eye className="btn-icon" size={14} />
                    <span>Open Company</span>
                  </button>
                </div>
                <div className="drawer-action-group">
                  <span className="drawer-group-label">Recovery</span>
                  <button type="button" className="secondary-button" onClick={() => setUnlockTarget(detailCompany)}>
                    <Lock className="btn-icon" size={14} />
                    <span>Unlock Owner</span>
                  </button>
                  <button type="button" className="secondary-button" onClick={() => setResetTarget(detailCompany)}>
                    <KeyRound className="btn-icon" size={14} />
                    <span>Reset Owner Password</span>
                  </button>
                </div>
                <div className="drawer-action-group drawer-action-group-danger">
                  <span className="drawer-group-label">Risky</span>
                  <button type="button" className="secondary-button" onClick={() => handleToggleStatus(detailCompany)}>
                    {detailCompany.is_active ? 'Disable Company' : 'Enable Company'}
                  </button>
                  <button type="button" className="danger-button" onClick={() => setConfirmTarget(detailCompany)}>
                    Delete Company
                  </button>
                </div>
              </div>
            </section>

            <section className="drawer-section">
              <div className="drawer-section-header">
                <h4>Workspace Rollout</h4>
                <span>{detailCompany.enabled_tab_count || 0} tabs enabled</span>
              </div>
              {workspaceLoadingId === detailCompany.id ? (
                <div className="status-message">Loading workspace access…</div>
              ) : (
                <>
                  <div className="drawer-badge-row">
                    {workspaceSummaryLabels.length > 0 ? workspaceSummaryLabels.map((label) => (
                      <span key={label} className="drawer-badge neutral">
                        <LayoutDashboard size={13} />
                        {label}
                      </span>
                    )) : (
                      <span className="status-message">Workspace tabs not loaded yet.</span>
                    )}
                  </div>
                  <button type="button" className="secondary-button" onClick={() => openWorkspaceAccess(detailCompany)}>
                    <Settings2 className="btn-icon" size={14} />
                    <span>Manage Workspace Access</span>
                  </button>
                </>
              )}
            </section>

            <section className="drawer-section">
              <div className="drawer-section-header">
                <h4>Recent Events</h4>
                <button
                  type="button"
                  className="link-like-button"
                  onClick={() => window.open(`/superadmin/audit`, '_blank', 'noopener')}
                >
                  Full audit log
                </button>
              </div>
              <div className="drawer-activity-list">
                {detailAuditLogs.length === 0 ? (
                  <div className="status-message">No recent events for this company.</div>
                ) : detailAuditLogs.map((entry) => (
                  <div key={entry.id} className="drawer-activity-row">
                    <div className="activity-icon"><Clock3 size={14} /></div>
                    <div className="activity-copy">
                      <strong>{normalizeAuditActionLabel(entry.action)}</strong>
                      <span>{formatRelativeTime(entry.created_at)} · {formatDateTime(entry.created_at)}</span>
                    </div>
                  </div>
                ))}
              </div>
            </section>
          </aside>
        </div>
      )}

      {confirmTarget && (
        <div className="modal-overlay">
          <div className="modal-card" role="dialog" aria-modal="true">
            <h3>Delete company?</h3>
            <p>You are about to delete <strong>{confirmTarget.name}</strong>. This removes all company data.</p>
            <label className="confirm-label">
              Type <strong>DELETE {confirmTarget.slug}</strong> to confirm.
              <input
                type="text"
                value={confirmText}
                onChange={(event) => setConfirmText(event.target.value)}
                placeholder={`DELETE ${confirmTarget.slug}`}
                autoFocus
              />
            </label>
            <label className="confirm-check">
              <input
                type="checkbox"
                checked={confirmChecked}
                onChange={(event) => setConfirmChecked(event.target.checked)}
              />
              I understand this action is irreversible.
            </label>
            <div className="modal-actions">
              <button
                type="button"
                className="secondary-button"
                onClick={closeConfirm}
                disabled={deletingId === confirmTarget.id}
              >
                Cancel
              </button>
              <button
                type="button"
                className="danger-button"
                onClick={() => {
                  handleDeleteCompany(confirmTarget);
                  closeConfirm();
                }}
                disabled={
                  deletingId === confirmTarget.id
                  || !confirmChecked
                  || confirmText.trim() !== `DELETE ${confirmTarget.slug}`
                }
              >
                {deletingId === confirmTarget.id ? 'Deleting...' : 'Delete'}
              </button>
            </div>
          </div>
        </div>
      )}

      {resetTarget && (
        <div className="modal-overlay">
          <div className="modal-card" role="dialog" aria-modal="true">
            <h3>Reset owner account password?</h3>
            <p>This will reset the primary owner account password for <strong>{resetTarget.name}</strong> to the configured default.</p>
            <div className="modal-actions">
              <button type="button" className="secondary-button" onClick={() => setResetTarget(null)}>
                Cancel
              </button>
              <button
                type="button"
                className="danger-button"
                onClick={() => {
                  handleResetAdmin(resetTarget);
                  setResetTarget(null);
                }}
              >
                Reset Password
              </button>
            </div>
          </div>
        </div>
      )}

      {unlockTarget && (
        <div className="modal-overlay">
          <div className="modal-card" role="dialog" aria-modal="true">
            <h3>Unlock owner account?</h3>
            <p>This will clear the primary owner account lock for <strong>{unlockTarget.name}</strong>.</p>
            <div className="modal-actions">
              <button type="button" className="secondary-button" onClick={() => setUnlockTarget(null)}>
                Cancel
              </button>
              <button
                type="button"
                className="primary-button"
                onClick={() => {
                  handleUnlockAdmin(unlockTarget);
                  setUnlockTarget(null);
                }}
              >
                Unlock Owner
              </button>
            </div>
          </div>
        </div>
      )}

      {labelsTarget && (
        <div className="modal-overlay">
          <div className="modal-card modal-large" role="dialog" aria-modal="true">
            <h3>Workspace Access for {labelsTarget.name}</h3>
            {labelsLoading ? (
              <div className="status-message">Loading workspace settings...</div>
            ) : labelsForm ? (
              <div className="label-editor">
                <div className="label-editor-section">
                  <h4>Navigation Tabs</h4>
                  <div className="label-grid">
                    {labelsForm.tabs.map((tab) => (
                      <div key={tab.id} className="label-row label-row-tab">
                        <span>{tab.id}</span>
                        <input
                          type="text"
                          value={tab.label}
                          onChange={(event) =>
                            setLabelsForm((prev) => ({
                              ...prev,
                              tabs: prev.tabs.map((item) =>
                                item.id === tab.id ? { ...item, label: event.target.value } : item
                              )
                            }))
                          }
                        />
                        <label className="label-visibility-toggle">
                          <input
                            type="checkbox"
                            checked={tab.visible !== false}
                            onChange={(event) =>
                              setLabelsForm((prev) => ({
                                ...prev,
                                tabs: prev.tabs.map((item) =>
                                  item.id === tab.id ? { ...item, visible: event.target.checked } : item
                                )
                              }))
                            }
                          />
                          <span>Enabled for company</span>
                        </label>
                      </div>
                    ))}
                  </div>
                </div>

                <div className="label-editor-section">
                  <h4>Transaction Fields</h4>
                  <div className="label-grid">
                    {labelsForm.fields.map((field) => (
                      <label key={field.key} className="label-row">
                        <span>{field.key}</span>
                        <input
                          type="text"
                          value={field.label}
                          onChange={(event) =>
                            setLabelsForm((prev) => ({
                              ...prev,
                              fields: prev.fields.map((item) =>
                                item.key === field.key ? { ...item, label: event.target.value } : item
                              )
                            }))
                          }
                        />
                      </label>
                    ))}
                  </div>
                </div>

                <div className="label-editor-section">
                  <h4>Settings Lists</h4>
                  {labelsForm.lists.length === 0 ? (
                    <div className="status-message">No lists yet.</div>
                  ) : (
                    <div className="label-grid">
                      {labelsForm.lists.map((list) => (
                        <label key={list.id} className="label-row">
                          <span>{list.list_key}</span>
                          <input
                            type="text"
                            value={list.label}
                            onChange={(event) =>
                              setLabelsForm((prev) => ({
                                ...prev,
                                lists: prev.lists.map((item) =>
                                  item.id === list.id ? { ...item, label: event.target.value } : item
                                )
                              }))
                            }
                          />
                        </label>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            ) : (
              <div className="status-message error">Unable to load workspace settings.</div>
            )}
            <div className="modal-actions">
              <button type="button" className="secondary-button" onClick={closeWorkspaceAccess}>
                Cancel
              </button>
              <button
                type="button"
                className="primary-button"
                onClick={handleSaveWorkspaceAccess}
                disabled={labelsLoading || labelsSaving || !labelsForm}
              >
                {labelsSaving ? 'Saving...' : 'Save Workspace Access'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default SuperAdminDashboard;
